:host(c-switch) {
  /**
   * @prop --c-switch-border-color: Border color
   * @prop --c-switch-handle-color: Handle color
   * @prop --c-switch-slider-color: Background color
   */
  --_c-switch-border-color: var(--c-switch-border-color, var(--c-tertiary-600));
  --_c-switch-handle-color: var(--c-switch-handle-color, var(--c-tertiary-600));
  --_c-switch-slider-color: var(--c-switch-slider-color, var(--c-transparent));

  /**
   * @prop --c-switch-border-color-disabled: Disabled border color
   * @prop --c-switch-handle-color-disabled: Disabled handle color
   * @prop --c-switch-slider-color-disabled: Disabled background color
   */
  --_c-switch-border-color-disabled: var(--c-switch-border-color-disabled, var(--c-transparent));
  --_c-switch-handle-color-disabled: var(--c-switch-handle-color-disabled, var(--c-tertiary-500));
  --_c-switch-slider-color-disabled: var(--c-switch-slider-color-disabled, var(--c-tertiary-200));

  /**
   * @prop --c-switch-slider-color-active: Active background color
   * @prop --c-switch-border-color-active: Active border color
   * @prop --c-switch-handle-color-active: Active handle color
   */
  --_c-switch-border-color-active: var(--c-switch-border-color-active, var(--c-primary-600));
  --_c-switch-handle-color-active: var(--c-switch-handle-color-active, var(--c-white));
  --_c-switch-slider-color-active: var(--c-switch-slider-color-active, var(--c-primary-600));

  /**
   * @prop --c-switch-border-color-active-disabled: Disabled active border color
   * @prop --c-switch-handle-color-active-disabled: Disabled active handle color
   * @prop --c-switch-slider-color-active-disabled: Disabled active background color
   */
  --_c-switch-border-color-active-disabled: var(--c-switch-border-color-active-disabled, var(--c-tertiary-400));
  --_c-switch-handle-color-active-disabled: var(--c-switch-handle-color-active-disabled, var(--c-white));
  --_c-switch-slider-color-active-disabled: var(--c-switch-slider-color-active-disabled, var(--c-tertiary-400));

  display: inline-block;
}

.c-switch {
  --_c-switch-handle-margin: 5px;
  --_c-switch-pointer: pointer;
  --_c-switch-height: 22px;
  --_c-switch-width: 44px;
  --_c-switch-handle-size: calc(var(--_c-switch-height) - 2 * var(--_c-switch-handle-margin));
  --_c-switch-handle-position-active: calc(
    var(--_c-switch-width) - var(--_c-switch-handle-size) - 2 * var(--_c-switch-handle-margin)
  );

  $this: &;

  backface-visibility: hidden;
  transform: translate3d(0, 0, 0);
  display: inline-grid;
  height: var(--_c-switch-height);
  position: relative;
  grid-template-columns: 1fr;
  gap: 12px;
  align-items: center;

  &--disabled {
    --_c-switch-pointer: default;
    --_c-switch-handle-color: var(--_c-switch-handle-color-disabled);
    --_c-switch-slider-color: var(--_c-switch-slider-color-disabled);
    --_c-switch-border-color: var(--_c-switch-border-color-disabled);
    --c-error-600: var(--c-tertiary-400);

    color: var(--c-tertiary-500);

    c-spinner {
      --c-color: var(--_c-switch-handle-color-disabled) !important;
    }
  }

  &--label {
    grid-template-columns: var(--_c-switch-width) auto;
  }

  &__label {
    span.required {
      color: var(--c-error-600);
    }
  }

  &__input {
    height: var(--_c-switch-height);
    position: relative;
    width: var(--_c-switch-width);
    align-self: start;
  }

  &__slider {
    background-color: var(--_c-switch-slider-color);
    border-radius: var(--_c-switch-height);
    box-shadow: inset 0 0 0 2px var(--_c-switch-border-color);
    inset: 0;
    cursor: var(--_c-switch-pointer);
    position: absolute;
    transform-origin: 50% 50%;
    transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);

    &::before {
      background-color: var(--_c-switch-handle-color);
      border-radius: 50%;
      bottom: var(--_c-switch-handle-margin);
      content: "";
      height: var(--_c-switch-handle-size);
      left: var(--_c-switch-handle-margin);
      position: absolute;
      transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
      width: var(--_c-switch-handle-size);
    }

    c-spinner {
      --c-color: var(--_c-switch-handle-color) !important;
      pointer-events: none;
      z-index: 2;
      position: absolute;
      left: 4px;
      top: 0;
      opacity: 0;
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    }

    &--loading {
      c-spinner {
        opacity: 1;
      }

      &::before {
        opacity: 0;
      }
    }
  }

  input {
    height: 0;
    opacity: 0;
    width: 0;

    &:checked {
      + {
        #{$this} {
          &__slider {
            --_c-switch-handle-color: var(--_c-switch-handle-color-active);
            --_c-switch-slider-color: var(--_c-switch-slider-color-active);
            --_c-switch-border-color: var(--_c-switch-border-color-active);

            &::before {
              transform: translateX(var(--_c-switch-handle-position-active));
            }

            c-spinner {
              --c-color: var(--_c-switch-handle-color-active) !important;

              transform: translateX(var(--_c-switch-handle-position-active));
            }

            &--disabled {
              --_c-switch-handle-color: var(--_c-switch-handle-color-active-disabled);
              --_c-switch-slider-color: var(--_c-switch-slider-color-active-disabled);
              --_c-switch-border-color: var(--_c-switch-border-color-active-disabled);

              c-spinner {
                --c-color: var(--_c-switch-handle-color-active-disabled) !important;

              }
            }
          }
        }
      }
    }

    &:focus {
      + {
        #{$this} {
          &__slider {
            outline: none;
          }
        }
      }
    }

    &:focus-visible {
      + {
        #{$this} {
          &__slider {
            outline: 2px var(--c-primary-600) solid;
            outline-offset: 2px;
          }
        }
      }
    }
  }
}
